import { ContainerEnvironment } from '@fluidx/office-fluid-types';
import { OfficeFluidContainerPackageName } from './Constants';
import { ContainerCodeLoadDetails, ContainerPreloadManifest, IExpandedFluidCodeDetails } from './types';
import {
  safeLocalStorage,
  fetchWithRetry,
  sendTelemetryEvent,
  constantBackoff,
  whitelist,
  resolveLocale,
  getCdnBaseUrl
} from '@fluidx/utilities';
import { createCodeDetailsProviderWithCache } from './createCodeDetailsProviderWithCache';
import { prefetchScript } from './prefetchScript';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { GenericEvent, GenericEventSubCategory } from './telemetry/GenericEvent';

const validOverrideEnvironments = [
  ContainerEnvironment.Gcc,
  ContainerEnvironment.Prod,
  ContainerEnvironment.Dogfood,
  ContainerEnvironment.Staging
];

function getOfficeContainerEnvironmentOverride(logger?: ITelemetryBaseLogger): ContainerEnvironment | undefined {
  const override = safeLocalStorage()?.OfficeContainerEnvironmentOverride;

  if (override !== undefined && !validOverrideEnvironments.includes(override)) {
    throw new Error(`OfficeContainerEnvironmentOverride local storage value has invalid value ${override}`);
  }

  // Log a generic event to correlate QoS telemetry with dev-flow scenarios
  // Task 4544147: Consider adding a helper method for the same
  override &&
    logger &&
    sendTelemetryEvent(logger, {
      eventName: GenericEvent.OfficeContainerEnvironmentOverrideUsed,
      subCategory: GenericEventSubCategory.LocalStorageOverride
    });

  return override;
}

/**
 * Preloads container resources to help improve performance of consumers of the office-fluid-container.
 */
export function preCacheOfficeContainerCode(containerCodeLoadDetails: ContainerCodeLoadDetails, locale?: string): void {
  let preloadManifest: ContainerPreloadManifest = containerCodeLoadDetails.preload.manifest;

  const assetsToPreFetch = [...preloadManifest.assets];

  if (preloadManifest.localizedAssets) {
    const resolvedLocale = resolveLocale(locale);

    const localizedAssets = preloadManifest.localizedAssets[resolvedLocale];

    if (localizedAssets) {
      assetsToPreFetch.push(...localizedAssets);
    }
  }

  assetsToPreFetch.forEach((asset) => prefetchScript(`${containerCodeLoadDetails.preload.jsBaseUrl}/${asset}`));
}

export interface OfficeContainerCodeLoadDetails extends ContainerCodeLoadDetails {
  // Metadata describing a container version that is unsupported. All active sessions on this container version or lower should be ended to avoid corruption.
  maxUnsupportedContainerScriptVersion: string;

  // An override for the fluid code proposal that's in the container for apps that want to load the latest version of the container.
  fluidCodeDetailsOverride: IExpandedFluidCodeDetails;
}

export async function getOfficeContainerCodeLoadDetailsFromNetwork(
  containerEnvironment: ContainerEnvironment,
  logger?: ITelemetryBaseLogger
): Promise<OfficeContainerCodeLoadDetails> {
  const containerEnvironmentToUse = getOfficeContainerEnvironmentOverride(logger) || containerEnvironment;

  const url = `${getCdnBaseUrl(containerEnvironmentToUse)}/container/versionless/officeContainerCodeLoadDetails.json`;

  const response = await fetchWithRetry(
    url,
    // TODO 5051864: Remeve `cache: "reload"`. Using as a way to burst the CDN cache that was accidentally set to one year.
    { method: 'GET', cache: 'reload' },
    // only retry if it's an offline fetch scenario
    { maxRetries: 3, filter: whitelist([709, 710]), backoffFn: constantBackoff(500) }
  );

  if (response.result.ok) {
    return response.result.json() as Promise<OfficeContainerCodeLoadDetails>;
  }
  throw new Error(`getOfficeFluidContainerOverride: Failed to fetch: ${url} ; StatusCode: ${response.result.status}`);
}

function isValidVersionNumber(version: number): boolean {
  return !isNaN(version) && isFinite(version);
}

function isCurrentContainerVersionUnsupported(
  currentVersion: string | number,
  maxUnsupportedVersion: string,
  latestCodeVersion: string
): boolean {
  const currentVersionAsNumber = parseFloat(currentVersion as any);
  if (!isValidVersionNumber(currentVersionAsNumber)) {
    return false;
  }

  const maxUnsupportedVersionAsNumber = parseFloat(maxUnsupportedVersion as any);
  if (!isValidVersionNumber(maxUnsupportedVersionAsNumber)) {
    return false;
  }

  // In the event that the currentVersion is greater than the latest code version, assume a rollback and force an upgrade
  const latestCodeVersionAsNumber = parseFloat(latestCodeVersion as any);
  if (isValidVersionNumber(latestCodeVersionAsNumber) && currentVersionAsNumber > latestCodeVersionAsNumber) {
    return true;
  }

  return currentVersionAsNumber <= maxUnsupportedVersionAsNumber;
}

export const getOfficeContainerCodeDetailsProvider = (logger?: ITelemetryBaseLogger, localeHint?: string) =>
  createCodeDetailsProviderWithCache(
    OfficeFluidContainerPackageName,
    {
      getLatestContainerCodeDetails: (containerEnvironment) =>
        getOfficeContainerCodeLoadDetailsFromNetwork(containerEnvironment, logger),
      preCacheContainerCode: preCacheOfficeContainerCode,
      upgradeNeeded: async (containerEnvironment, currentVersion) => {
        try {
          const containerDetails = await getOfficeContainerCodeLoadDetailsFromNetwork(containerEnvironment, logger);
          return isCurrentContainerVersionUnsupported(
            currentVersion,
            containerDetails.maxUnsupportedContainerScriptVersion,
            containerDetails.fluidCodeDetailsOverride.package.version
          );
        } catch {
          return false;
        }
      }
    },
    logger,
    localeHint
  );
